import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import 'remixicon/fonts/remixicon.css'

export default function Services() {
  const mainServices = [
    {
      icon: 'ri-smartphone-line',
      title: 'Online Ordering Platform',
      description: 'A structured and secure platform allowing customers to browse verified restaurants, compare menus, and place orders conveniently.',
      color: 'from-orange-400 to-orange-600',
      bgColor: 'from-orange-50 to-orange-100'
    },
    {
      icon: 'ri-truck-line',
      title: 'Delivery & Logistics',
      description: 'We operate a dependable delivery network designed to ensure meals arrive fresh, timely, and professionally handled.',
      color: 'from-green-400 to-green-600',
      bgColor: 'from-green-50 to-green-100'
    },
    {
      icon: 'ri-store-2-line',
      title: 'Vendor Marketplace Solutions',
      description: 'We empower restaurants and food vendors by providing:',
      features: [
        'Expanded digital visibility',
        'Order management systems',
        'Delivery integration',
        'Sales growth opportunities'
      ],
      color: 'from-purple-400 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100'
    },
    {
      icon: 'ri-group-line',
      title: 'Corporate Catering & Bulk Orders',
      description: 'We facilitate structured meal solutions for:',
      features: [
        'Corporate offices',
        'Events & conferences',
        'Workshops & seminars',
        'Institutional clients'
      ],
      color: 'from-blue-400 to-blue-600',
      bgColor: 'from-blue-50 to-blue-100'
    }
  ]

  return (
    <div className="pt-20">
      <SEO 
        title="Our Services - Food Delivery, Corporate Catering & Vendor Solutions | Ami Bites"
        description="Explore Ami Bites services: online food ordering platform, reliable delivery logistics, vendor marketplace solutions, and corporate catering for events & bulk orders in Zimbabwe."
        keywords="food delivery services Zimbabwe, corporate catering Zimbabwe, bulk food orders, vendor marketplace, restaurant delivery logistics, online ordering platform, event catering Zimbabwe"
        canonical="https://amibites.com/services"
      />
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-3xl shadow-xl overflow-hidden group"
              >
                <div className={`h-2 bg-gradient-to-r ${service.color}`}></div>
                
                <div className="p-8">
                  <motion.div
                    initial={{ rotate: -180, scale: 0 }}
                    whileInView={{ rotate: 0, scale: 1 }}
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                    className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${service.color} rounded-2xl mb-6 shadow-lg group-hover:shadow-2xl`}
                  >
                    <i className={`${service.icon} text-4xl text-white`}></i>
                  </motion.div>

                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>

                  {service.features && (
                    <div className="space-y-3">
                      {service.features.map((feature, fIndex) => (
                        <motion.div
                          key={fIndex}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: fIndex * 0.1 }}
                          className="flex items-center space-x-3"
                        >
                          <div className={`w-2 h-2 bg-gradient-to-r ${service.color} rounded-full`}></div>
                          <span className="text-gray-700">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-orange-600 to-green-600 rounded-3xl p-12 text-white text-center shadow-2xl relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
            </div>

            <div className="relative">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-3xl md:text-4xl font-bold mb-4"
              >
                Ready to Experience Our Services?
              </motion.h2>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-xl mb-8 opacity-90"
              >
                Join thousands of satisfied customers and partner restaurants
              </motion.p>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/order">
                  <motion.button
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-white text-orange-600 rounded-xl font-semibold text-lg shadow-lg hover:shadow-2xl transition-shadow flex items-center justify-center space-x-2"
                  >
                    <span>Start Ordering</span>
                    <i className="ri-shopping-bag-line text-xl"></i>
                  </motion.button>
                </Link>
                <Link to="/partner-with-us">
                  <motion.button
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>Become a Partner</span>
                    <i className="ri-hand-heart-line text-xl"></i>
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
